import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { markPWAPrompted, markFirstVisitDone, saveSettings } from '../store/slices/uiSlice';

interface PWAInstallState {
  isInstallable: boolean;
  showPrompt: boolean;
  isMobile: boolean;
  isStandalone: boolean;
}

export const usePWAInstall = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { hasPromptedPWA, firstVisitDone } = useSelector((state: RootState) => state.ui);
  
  const [state, setState] = useState<PWAInstallState>({
    isInstallable: false,
    showPrompt: false,
    isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
    isStandalone: window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true,
  });

  useEffect(() => {
    const checkPWAStatus = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          (window.navigator as any).standalone === true;
      const isInstallable = (window as any).deferredPrompt !== undefined;

      // 初回訪問（firstVisitDoneがfalse）かつモバイルかつ未インストールかつ未プロンプト
      const showPrompt = !firstVisitDone && state.isMobile && !isStandalone && !hasPromptedPWA;

      setState(prev => ({
        ...prev,
        isInstallable,
        isStandalone,
        showPrompt,
      }));

      // 初回訪問フラグを立てる（UIを表示した瞬間などに実行）
      if (!firstVisitDone) {
        dispatch(markFirstVisitDone());
        dispatch(saveSettings({ firstVisitDone: true }));
      }
    };

    checkPWAStatus();

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      (window as any).deferredPrompt = e;
      setState(prev => ({ ...prev, isInstallable: true }));
    };

    const handleAppInstalled = () => {
      dispatch(markPWAPrompted());
      dispatch(saveSettings({ hasPromptedPWA: true }));
      setState(prev => ({ ...prev, isStandalone: true, showPrompt: false }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [dispatch, firstVisitDone, hasPromptedPWA, state.isMobile]);

  const hidePrompt = () => {
    dispatch(markPWAPrompted());
    dispatch(saveSettings({ hasPromptedPWA: true }));
    setState(prev => ({ ...prev, showPrompt: false }));
  };

  const installPWA = async () => {
    const deferredPrompt = (window as any).deferredPrompt;
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      (window as any).deferredPrompt = null;
      setState(prev => ({ ...prev, isInstallable: false }));
    }
    hidePrompt();
  };

  return {
    ...state,
    hidePrompt,
    installPWA,
  };
};
